"use client";

import type React from 'react';
import Image from 'next/image';
import { assets } from '@/app/lib/assets/assets';
import Link from 'next/link';

interface KeyFeatureCardProps {
  title: string;
  description: React.ReactNode;
  imageSrc: string;
  altText: string;
}

const KeyFeatureCard: React.FC<KeyFeatureCardProps> = ({
  title,
  description,
  imageSrc,
  altText,
}) => {
  return (
    <div className="bg-[#121212]/80 rounded-xl p-6 md:p-8 border border-white/20 hover:border-white/50 shadow-[0_0_8px_rgba(255,255,255,0.15)] transition-all duration-300 flex flex-col h-full">
      <div className="mb-6">
        <h3 className="text-xl font-semibold mb-3 text-gray-100">{title}</h3>
        <p className="text-gray-300">{description}</p>
      </div>
      <div className="relative h-44 md:h-52 w-full mt-auto">
        <Image
          src={imageSrc}
          alt={altText}
          fill
          className="object-contain scale-110"
        />
      </div>
    </div>
  );
};

const KeyFeaturesSection = () => {
  return (
    <div className="bg-[#121212] text-white pt-0 pb-[12.8px] md:pb-[32px]">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-[85%] mx-auto">
          <KeyFeatureCard
            title="Sync With Your Calendar"
            description="MeetMinutes effortlessly integrates with your existing Google or Microsoft calendar and works with Google Meet, MS Teams, Zoom, and more."
            imageSrc={assets.productCapabilities.calendarSync}
            altText="Calendar integration"
          />

          <KeyFeatureCard
            title="Multilingual Language Support"
            description={(
              <>
                The only accurate solution in the market that supports {" "}
                <Link href="/languages" className="text-blue-400 hover:text-blue-300 transition-colors underline">
                  multiple languages
                </Link> in the same meeting.
              </>
            )}
            imageSrc={assets.productCapabilities.multilingual}
            altText="Multilingual support"
          />

          <KeyFeatureCard
            title="Actionable Insights, No Effort Required"
            description="Automated transcriptions, task tracking, and key insights. No more manual note-taking."
            imageSrc={assets.productCapabilities.insights}
            altText="Actionable insights"
          />
        </div>
      </div>
    </div>
  );
};

export default KeyFeaturesSection; 